// Declarative runtime lenses projected into the emission contract — the same
// shape the artifact tier produces, so the behaviour graph ingests ONE schema
// regardless of implementation tier. The declarative lens is the degenerate
// case: fields become roles by inference, checks become claims, the spec's
// content hash becomes the version.

import {
  contentVersion,
  validateEmissionBatch,
  entitiesFromValues,
} from "../../xray-factory/src/emit/contract.js";

/**
 * The contract manifest a declarative spec implies.
 *
 * Deliberately STRICT: a spec missing judgment metadata (consumer, decision,
 * falsifier) produces a manifest the validator refuses, and the reading says
 * so — a lens that cannot say who reads it cannot index into the graph, and
 * papering that over with a placeholder would launder the omission.
 */
export function emissionManifestFromSpec(spec) {
  const s = spec && typeof spec === "object" ? spec : {};
  const emits = {};
  const groupBy = s.group?.by;
  for (const [name, path] of Object.entries(s.fields || {})) {
    // The transport tri-state is host-stamped on the occurrence (`call`),
    // never a declared field — the contract-level form of the
    // `call`-not-`outcome` lesson.
    if (path === "outcome") continue;
    if (name === groupBy) {
      // The grouping identity is the target. Durability follows the axis
      // decision the judge already made: a tab-bearing lens claimed a target
      // that outlives its session; a trace-only one did not.
      emits[name] = { role: "target", durable: s.tab !== false };
      continue;
    }
    emits[name] = { role: path === "output" || path === "command" ? "evidence" : "binding" };
  }
  return {
    behaviour: s.id,
    version: contentVersion(JSON.stringify(s ?? null)),
    name: s.label || s.id || "",
    consumer: s.consumer || "",
    decision: s.decision || "",
    falsifier: s.draftedFrom?.falsifier || "",
    substrate: {
      tools: Array.isArray(s.match?.tools) ? s.match.tools : [],
      shell: Boolean(s.match?.shell),
    },
    claims: (Array.isArray(s.checks) ? s.checks : []).map((c) => ({ name: String(c?.name || "").trim() })),
    emits,
  };
}

/**
 * Project extracted ops into occurrences. Values are picked by the manifest's
 * schema — anything the op carries beyond it stays behind, which is the point:
 * the emission is the lens's public statement, not its working memory.
 */
export function occurrencesFromOps(manifest, ops) {
  const fieldNames = Object.keys(manifest?.emits || {});
  return (ops || []).map((op) => {
    const values = {};
    for (const name of fieldNames) {
      const v = op?.[name];
      if (v === undefined || v === null || v === "") continue;
      values[name] = typeof v === "number" || typeof v === "boolean" ? v : String(v);
    }
    const claims = {};
    for (const c of op?.checks || []) claims[c.name] = Boolean(c.ok);
    const occ = {
      behaviour: manifest.behaviour,
      version: manifest.version,
      key: op?.key,
      call: op?.outcome === "errored" ? "errored" : op?.outcome === "answered" ? "answered" : "unrecorded",
      ...(op?.sessionId ? { sessionId: op.sessionId } : {}),
      ...(op?.endpointId ? { endpointId: op.endpointId } : {}),
      ...(op?.ts ? { ts: op.ts } : {}),
      values,
      claims,
    };
    const entities = entitiesFromValues(manifest, occ);
    return entities.length ? { ...occ, entities } : occ;
  });
}

/**
 * The whole projection, boundary-validated. `invalid` and `manifestProblems`
 * come back beside the valid rows because the three failure classes must not
 * share a sentence: a spec that cannot manifest, a row that violated the
 * schema, and an honest empty window are different findings.
 */
export function emitFromSpec(spec, ops) {
  const manifest = emissionManifestFromSpec(spec);
  const occurrences = occurrencesFromOps(manifest, ops);
  const { manifestProblems, valid, invalid } = validateEmissionBatch(manifest, occurrences);
  return { manifest, manifestProblems, occurrences: valid, invalid };
}
